import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { AuthGuard, EmailGuard } from "../App";
import { Oval } from "react-loader-spinner";
import { FaSort, FaAngleDoubleLeft, FaAngleLeft,FaTrashAlt, FaAngleRight, FaAngleDoubleRight } from "react-icons/fa";
import { StandardURL } from "../App";

const ViewTrashBillsPage = () => {
  const [trashBills, setTrashBills] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10); // Number of items per page
  const navigate = useNavigate();

  useEffect(() => {
    fetchTrashBills();
  }, []);

  const fetchTrashBills = async () => {
    setIsLoading(true);
    setError(null);
    const token = localStorage.getItem("token");
    try {
      const response = await axios.get(
        `${StandardURL}/iNyumba/admin/trashbill/fetchalltrashbill`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (response.data.response && Array.isArray(response.data.response)) {
        setTrashBills(response.data.response);
        toast.success("Trash bills fetched successfully!");
      } else {
        setTrashBills([]);
      }
    } catch (err) {
      setError("Failed to fetch trash bills.");
      toast.error(err.response?.data?.response || "Failed to fetch trash bills.");
      console.error("Error fetching trash bills:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteTrashFee = async (propertyId) => {
    if (window.confirm("Are you sure you want to delete this trash bill?")) {
      setIsLoading(true);
      const token = localStorage.getItem("token");
      try {
        const response = await axios.delete(
          `${StandardURL}/iNyumba/trashfee/deletetrashfee/${propertyId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        toast.success(response.data.response);
        // Remove deleted item from the local state
        setTrashBills(prevBills => prevBills.filter(fee => fee.propertyid !== propertyId));
      } catch (err) {
        toast.error(err.response?.data?.response || "Failed to delete trash bill.");
        console.error("Error deleting trash bill:", err);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return null;
    return sortConfig.direction === 'asc' ? ' ▲' : ' ▼';
  };

  // Sorting logic
  const sortedBills = [...trashBills].sort((a, b) => {
    if (sortConfig.key !== null) {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];
      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
    }
    return 0;
  });

  // Pagination logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = sortedBills.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(sortedBills.length / itemsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  // Pagination UI rendering logic
  const pageNumbers = [];
  const maxPageNumbersToShow = 5;
  let startPage = Math.max(1, currentPage - Math.floor(maxPageNumbersToShow / 2));
  let endPage = Math.min(totalPages, startPage + maxPageNumbersToShow - 1);

  if (endPage - startPage + 1 < maxPageNumbersToShow) {
    startPage = Math.max(1, endPage - maxPageNumbersToShow + 1);
  }

  for (let i = startPage; i <= endPage; i++) {
    pageNumbers.push(i);
  }

  return (
    <EmailGuard>
      <AuthGuard>
        <div className="container mx-auto px-4 py-8 bg-gray-100 min-h-screen">
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-3xl font-bold text-gray-800">Trash Bills</h1>
          </div>

          <div className="bg-white shadow-md rounded-lg overflow-hidden">
            {isLoading ? (
              <div className="flex justify-center items-center p-8">
                <Oval color="#4F46E5" height={50} width={50} />
              </div>
            ) : error ? (
              <div className="text-center p-8 text-red-500 font-semibold">
                {error}
              </div>
            ) : trashBills.length === 0 ? (
              <div className="text-center p-8 text-gray-500">
                No trash bills available.
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        {["trashbillid", "propertyid", "rentalid", "housenumber", "userid", "amount", "created_at", "updated_at"].map((header) => (
                          <th
                            key={header}
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                            onClick={() => requestSort(header)}
                          >
                            <div className="flex items-center">
                              {header.replace(/_/g, ' ').toUpperCase()}
                              <FaSort className="ml-1 text-gray-400" />
                              {getSortIcon(header)}
                            </div>
                          </th>
                        ))}
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {currentItems.map((trashBill) => (
                        <tr key={trashBill.trashbillid}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{trashBill.trashbillid}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{trashBill.propertyid}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{trashBill.rentalid}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{trashBill.housenumber}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{trashBill.userid}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-semibold">Ksh {trashBill.amount}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{new Date(trashBill.created_at).toLocaleString()}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{new Date(trashBill.updated_at).toLocaleString()}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <button
                              onClick={() => handleDeleteTrashFee(trashBill.propertyid)}
                              className="text-red-600 hover:text-red-900"
                              title="Delete Trash Bill"
                            >
                              <FaTrashAlt className="h-5 w-5" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {/* Pagination Controls */}
                {totalPages > 1 && (
                  <nav className="flex items-center justify-center space-x-2 mt-6 p-4">
                    <button
                      onClick={() => paginate(1)}
                      disabled={currentPage === 1}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white rounded-md border border-gray-300 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <FaAngleDoubleLeft />
                    </button>
                    <button
                      onClick={() => paginate(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white rounded-md border border-gray-300 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <FaAngleLeft />
                    </button>
                    {pageNumbers.map(number => (
                      <button
                        key={number}
                        onClick={() => paginate(number)}
                        className={`px-4 py-2 text-sm font-medium rounded-md border border-gray-300 transition-colors duration-150 ease-in-out
                          ${currentPage === number 
                            ? 'bg-indigo-600 text-white border-indigo-600 hover:bg-indigo-700' 
                            : 'text-gray-700 bg-white hover:bg-gray-100'}`}
                      >
                        {number}
                      </button>
                    ))}
                    <button
                      onClick={() => paginate(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white rounded-md border border-gray-300 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <FaAngleRight />
                    </button>
                    <button
                      onClick={() => paginate(totalPages)}
                      disabled={currentPage === totalPages}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white rounded-md border border-gray-300 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <FaAngleDoubleRight />
                    </button>
                  </nav>
                )}
              </>
            )}
          </div>
        </div>
      </AuthGuard>
    </EmailGuard>
  );
};

export default ViewTrashBillsPage;